// PKCE (RFC 7636) and `state` for the hosted sign-in's authorization code grant. AWS recommends the code grant with
// PKCE for browser apps: a sign-in code intercepted on its way back is useless without the verifier, and the
// verifier never leaves this tab. `state` makes sure the code that comes back answers a request this tab made.
// Web Crypto is there on https pages and on localhost only.
const VERIFIER_KEY = 'pkce_verifier';
const STATE_KEY = 'auth_state';

function webCrypto() {
  const crypto = window.crypto;
  if (!crypto?.subtle || !crypto.getRandomValues) {
    throw new Error("This browser can't sign in securely: Web Crypto is unavailable. Open the app over https.");
  }
  return crypto;
}

const base64url = (bytes) =>
  window.btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const randomString = (size) => base64url(webCrypto().getRandomValues(new Uint8Array(size)));

export const createVerifier = () => randomString(32);

export async function challengeFor(verifier) {
  const digest = await webCrypto().subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

// What every sign-in redirect to /oauth2/authorize carries. No `scope`, so the app client's allowed scopes apply.
export async function authorizeParams({ clientId, redirectUri }) {
  const verifier = createVerifier();
  const state = randomString(16);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  return new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri,
    state,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: 'S256',
  });
}

// The verifier for a code that came back with `state`, or null when this tab did not send that state. Either way
// both are used up: a code is exchanged once.
export function takeVerifier(state) {
  const expected = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  forgetPkce();
  return expected && state === expected ? verifier : null;
}

export function forgetPkce() {
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
}
